import {
  Component,
  ChangeDetectionStrategy,
  Injectable,
  inject,
  signal,
} from '@angular/core';
import { IonSpinner } from '@ionic/angular/standalone';

/**
 * LoadingOverlayService — Controls the full-screen loading overlay.
 *
 * Usage:
 * ```ts
 * const loading = inject(LoadingOverlayService);
 * loading.show('Completando orden...');
 * await doWork();
 * loading.hide();
 * ```
 */
@Injectable({ providedIn: 'root' })
export class LoadingOverlayService {
  /** Whether the overlay is currently visible */
  readonly visible = signal(false);

  /** The contextual message displayed below the spinner */
  readonly message = signal('Cargando...');

  /** Show the overlay with an optional contextual message */
  show(message = 'Cargando...'): void {
    this.message.set(message);
    this.visible.set(true);
  }

  /** Hide the overlay */
  hide(): void {
    this.visible.set(false);
  }
}

/**
 * LoadingOverlayComponent — Full-screen semitransparent overlay with spinner.
 *
 * Blocks user interaction during critical async operations:
 * - Completing an order
 * - Status transitions (Estoy en camino, Iniciar)
 * - Logging out
 * - Clearing local data
 *
 * Placed once in app.component.html. Controlled via LoadingOverlayService.
 *
 * Accessibility: aria-busy, aria-live for screen readers.
 * Respects prefers-reduced-motion (no fade animation).
 */
@Component({
  selector: 'fsm-loading-overlay',
  standalone: true,
  imports: [IonSpinner],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (service.visible()) {
      <div
        class="fsm-loading-overlay"
        role="alertdialog"
        aria-busy="true"
        aria-live="assertive"
        aria-label="Cargando"
      >
        <div class="fsm-loading-overlay__card">
          <ion-spinner name="crescent" class="fsm-loading-overlay__spinner" />
          <span class="fsm-loading-overlay__message">{{ service.message() }}</span>
        </div>
      </div>
    }
  `,
  styles: [`
    .fsm-loading-overlay {
      position: fixed;
      inset: 0;
      z-index: 99999;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(0, 0, 0, 0.4);
      backdrop-filter: blur(2px);
      -webkit-backdrop-filter: blur(2px);
      animation: fsm-overlay-fade-in 200ms ease-out;
    }

    .fsm-loading-overlay__card {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 16px;
      padding: 32px 40px;
      background: #ffffff;
      border-radius: 16px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
    }

    .fsm-loading-overlay__spinner {
      width: 40px;
      height: 40px;
      color: var(--fsm-primary-500, #3b82f6);
    }

    .fsm-loading-overlay__message {
      font-size: 14px;
      font-weight: 500;
      color: var(--fsm-neutral-700, #374151);
      text-align: center;
      max-width: 200px;
    }

    @keyframes fsm-overlay-fade-in {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @media (prefers-reduced-motion: reduce) {
      .fsm-loading-overlay {
        animation: none;
      }
    }
  `],
})
export class LoadingOverlayComponent {
  protected readonly service = inject(LoadingOverlayService);
}
